import { useEffect } from "react";

/**
 * The one and only string the browser tab may show. Mirrored as a LITERAL in
 * `packages/app/public/index.html` (the first paint, before any JS runs) — keep the two in step.
 */
export const TAB_TITLE = "IM IDP";

/**
 * What the tab is allowed to read: the title, optionally behind plugin-notifications' unread-count
 * prefix. Tolerating `(3) ` is what stops the two title writers below from fighting forever.
 */
const ALLOWED_TITLE = /^(\(\d+\)\s)?IM IDP$/;

const COUNT_PREFIX = /^\(\d+\)\s/;

// WHY A MUTATION OBSERVER AND NOT SOME CONFIG KEY.
//
// Every Backstage page renders `<Header>` from @backstage/core-components, and Header renders a
// react-helmet with `titleTemplate = "<page> | %s | <app.title>"` (see its Header.esm.js). So the tab
// said "Templates | IM Developer Portal" — and in a narrow tab all a colleague can read is the page
// name, which is why the tab looked like it belonged to a different app on every route.
//
// There is no `app.*` setting for this: `app.title` is only the TAIL of that template, so changing it
// cannot remove the page name. And an extra react-helmet mounted here does not win either — helmet
// resolves conflicting titles by taking the INNERMOST (last-mounted) instance, and an app-root element
// mounts before the page it wraps, so the page's Header always overrides it.
//
// Watching the <title> element and putting it back is therefore the only place that outranks every
// page. It is also the pattern upstream already uses for exactly this element:
// @backstage/plugin-notifications' useTitleCounter observes <title> to maintain its "(n) " prefix.
//
// The two writers coexist without a loop: notifications prepends its count to whatever it finds, we
// accept the counted form via ALLOWED_TITLE, and our own write settles on the first re-entry because
// the value it produces is one ALLOWED_TITLE matches. Do NOT tighten that regex to the bare title
// without also taking the notification counter out — the two then overwrite each other every 100ms
// (useTitleCounter throttles) for as long as an unread notification exists.
export const TabTitle = () => {
	useEffect(() => {
		const titleElement = document.querySelector("title");
		if (!titleElement) {
			return undefined;
		}

		const pin = () => {
			const current = titleElement.textContent ?? "";
			if (ALLOWED_TITLE.test(current)) {
				return;
			}
			// Preserve any unread count that was already there, so pinning the title never silently
			// clears the notification badge.
			const count = COUNT_PREFIX.exec(current)?.[0] ?? "";
			titleElement.textContent = `${count}${TAB_TITLE}`;
		};

		const observer = new MutationObserver(pin);
		observer.observe(titleElement, { characterData: true, childList: true, subtree: true });

		// The first page's Header may have written its title before this effect ran.
		pin();

		return () => observer.disconnect();
	}, []);

	// An app-root element has to render something; this one only has side effects.
	return null;
};
